import React, { useState, useEffect, useCallback } from 'react';
import { Hash, Check, Copy, GripVertical, X, ChevronDown } from 'lucide-react';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = window.__WR_API_BASE__;

/* Official ICD-10-CM description reference — corrects LLM hallucinations */
const ICD10_OFFICIAL_DESCRIPTIONS = {
  'Z76.0': 'Encounter for issue of repeat prescription',
  'Z71.2': 'Person consulting for explanation of examination or test findings',
  'Z71.89': 'Other specified counseling',
  'Z71.1': 'Person with feared health complaint in whom no diagnosis is made',
  'Z00.00': 'Encounter for general adult medical examination without abnormal findings',
  'Z00.01': 'Encounter for general adult medical examination with abnormal findings',
  'Z02.89': 'Encounter for other administrative examinations',
  'Z09': 'Encounter for follow-up examination after completed treatment for conditions other than malignant neoplasm',
  'Z79.899': 'Other long term (current) drug therapy',
  'Z79.01': 'Long term (current) use of anticoagulants',
  'Z79.891': 'Long term (current) use of opiate analgesic',
  'Z79.82': 'Long term (current) use of aspirin',
  'E11.9': 'Type 2 diabetes mellitus without complications',
  'E11.65': 'Type 2 diabetes mellitus with hyperglycemia',
  'E55.9': 'Vitamin D deficiency, unspecified',
  'E53.8': 'Deficiency of other specified B group vitamins',
  'E78.0': 'Pure hypercholesterolemia',
  'E78.1': 'Pure hypertriglyceridemia',
  'E78.2': 'Mixed hyperlipidemia',
  'E78.5': 'Hyperlipidemia, unspecified',
  'E03.9': 'Hypothyroidism, unspecified',
  'E05.90': 'Thyrotoxicosis, unspecified without thyrotoxic crisis or storm',
  'R73.03': 'Prediabetes',
  'R73.01': 'Impaired fasting glucose',
  'R73.09': 'Other abnormal glucose',
  'R74.01': 'Elevation of liver transaminase levels',
  'R97.20': 'Elevated prostate specific antigen [PSA]',
  'D50.9': 'Iron deficiency anemia, unspecified',
  'D64.9': 'Anemia, unspecified',
  'D72.829': 'Elevated white blood cell count, unspecified',
  'D69.6': 'Thrombocytopenia, unspecified',
  'N18.3': 'Chronic kidney disease, stage 3 (moderate)',
  'N18.9': 'Chronic kidney disease, unspecified',
  'M54.50': 'Low back pain, unspecified',
  'M54.5': 'Low back pain',
  'M25.511': 'Pain in right shoulder',
  'M25.512': 'Pain in left shoulder',
  'M25.561': 'Pain in right knee',
  'M25.562': 'Pain in left knee',
  'M25.551': 'Pain in right hip',
  'M25.552': 'Pain in left hip',
  'G43.909': 'Migraine, unspecified, not intractable, without status migrainosus',
  'F32.0': 'Major depressive disorder, single episode, mild',
  'F32.1': 'Major depressive disorder, single episode, moderate',
  'F32.9': 'Major depressive disorder, single episode, unspecified',
  'F41.1': 'Generalized anxiety disorder',
  'F41.9': 'Anxiety disorder, unspecified',
  'F43.10': 'Post-traumatic stress disorder, unspecified',
  'J30.1': 'Allergic rhinitis due to pollen',
  'J30.2': 'Other seasonal allergic rhinitis',
  'J30.9': 'Allergic rhinitis, unspecified',
  'J06.9': 'Acute upper respiratory infection, unspecified',
  'J01.90': 'Acute sinusitis, unspecified',
  'K21.0': 'Gastro-esophageal reflux disease with esophagitis',
  'K21.9': 'Gastro-esophageal reflux disease without esophagitis',
  'L50.9': 'Urticaria, unspecified',
  'I10': 'Essential (primary) hypertension',
  'G47.00': 'Insomnia, unspecified',
  'R80.9': 'Proteinuria, unspecified',
};

/** Correct an ICD-10 code's description using the official reference */
const correctDescription = (code) => {
  if (!code) return code;
  const official = ICD10_OFFICIAL_DESCRIPTIONS[code.code?.toUpperCase()];
  if (official) {
    return { ...code, description: official };
  }
  return code;
};

/* Sortable ICD-10 pill */
const SortableCodePill = ({ code, isPrimary, isManual, index, onDelete, onTogglePrimary, resolvedDesc }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: code.code });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 1,
  };

  const displayDesc = (isManual && resolvedDesc) ? resolvedDesc : code.description;
  const tooltipText = isPrimary 
    ? `Primary: ${displayDesc} - Click to remove` 
    : `${displayDesc} - Click to mark as PRIMARY`;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-1 px-2 py-1 border rounded transition-opacity ${
        isManual ? 'bg-blue-500/10 border-blue-500/30' :
        isPrimary ? 'bg-amber-500/10 border-amber-500/30' : 'bg-emerald-500/10 border-emerald-500/30'
      }`}
      data-testid={`icd10-code-${index}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing touch-none shrink-0"
        data-testid={`icd10-drag-handle-${code.code}`}
      >
        <GripVertical className="w-3 h-3 text-muted-foreground" />
      </button>
      <button
        className="flex items-center gap-1 cursor-pointer hover:opacity-80"
        onClick={() => onTogglePrimary ? onTogglePrimary(code.code) : null}
        title={tooltipText}
      >
        {isPrimary && <span className="text-amber-400 font-bold text-[10px]">1°</span>}
        <span className={`font-mono text-xs font-medium ${
          isManual ? 'text-blue-400' : isPrimary ? 'text-amber-400' : 'text-emerald-400'
        }`}>
          {code.code}
        </span>
        {isManual && resolvedDesc && (
          <span className="text-[9px] text-blue-300/70 ml-0.5 max-w-[120px] truncate">{resolvedDesc}</span>
        )}
      </button>
      {onDelete && (
        <button
          className="shrink-0 cursor-pointer hover:text-red-400 text-muted-foreground transition-colors"
          onClick={() => onDelete(code.code)}
          title="Remove code"
          data-testid={`icd10-delete-${code.code}`}
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

/**
 * ICD10CodesDisplay - Displays ICD-10 codes with drag-to-reorder and delete
 */
const ICD10CodesDisplay = ({
  icd10Suggestions = [],
  manualIcd10 = '',
  primaryIcd10Codes = [],
  copiedField,
  onCopy,
  onReorder,
  onDelete,
  liveIcd10Suggestions,
  setPrimaryIcd10Codes,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Resolved descriptions for manual codes { "E11.9": "Type 2 diabetes..." }
  const [resolvedDescs, setResolvedDescs] = useState({});

  // Use liveIcd10Suggestions (which respects manual reorder) if available, else fall back to result.icd10_suggestions
  // Apply official description corrections to prevent SNOMED/hallucinated descriptions
  const aiCodes = (liveIcd10Suggestions || icd10Suggestions || []).map(correctDescription);
  
  // Convert manual codes to same format as AI codes so they're all sortable together
  const manualCodeObjects = manualIcd10 
    ? manualIcd10.split(',').map(c => c.trim()).filter(Boolean)
        .filter(c => !aiCodes.some(ac => ac.code === c))
        .map(c => ({ code: c, description: resolvedDescs[c.toUpperCase()] || 'Manual', relevance: 'manual' }))
    : [];

  // Resolve manual code descriptions when manual codes change
  const resolveManualCodes = useCallback(async (codes) => {
    const unresolvedCodes = codes.filter(c => !resolvedDescs[c.toUpperCase()]);
    if (unresolvedCodes.length === 0) return;
    
    try {
      const res = await fetch(`${API}/api/icd10/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ codes: unresolvedCodes }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.results && Object.keys(data.results).length > 0) {
          setResolvedDescs(prev => ({ ...prev, ...data.results }));
        }
      }
    } catch (e) {
      // Silent fail — tooltip just stays as "Manual"
    }
  }, [resolvedDescs]);

  useEffect(() => {
    if (manualIcd10) {
      const codes = manualIcd10.split(',').map(c => c.trim()).filter(Boolean);
      const unresolved = codes.filter(c => !resolvedDescs[c.toUpperCase()]);
      if (unresolved.length > 0) {
        resolveManualCodes(unresolved);
      }
    }
  }, [manualIcd10]); // eslint-disable-line react-hooks/exhaustive-deps

  const primaryCodes = aiCodes.filter(c => primaryIcd10Codes.includes(c.code));
  const secondaryCodes = aiCodes.filter(c => !primaryIcd10Codes.includes(c.code));
  // All codes in one sortable list: primary first, then secondary, then manual
  // Z71.89 ALWAYS pinned to last position
  const allCodes = [...primaryCodes, ...secondaryCodes, ...manualCodeObjects];
  const z7189 = allCodes.filter(c => c.code === 'Z71.89');
  const nonZ7189 = allCodes.filter(c => c.code !== 'Z71.89');
  const sortedCodes = [...nonZ7189, ...z7189];

  const getAllCodesText = () => {
    return sortedCodes.map(c => {
      if (c.relevance === 'manual') {
        const desc = resolvedDescs[c.code.toUpperCase()];
        return desc ? `${c.code}: ${desc}` : c.code;
      }
      return `${c.code}: ${c.description}`;
    }).join('\n');
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id && onReorder) {
      const oldIndex = sortedCodes.findIndex(s => s.code === active.id);
      const newIndex = sortedCodes.findIndex(s => s.code === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        let reordered = arrayMove(sortedCodes, oldIndex, newIndex);
        // Always pin Z71.89 to last
        const z89 = reordered.filter(c => c.code === 'Z71.89');
        const rest = reordered.filter(c => c.code !== 'Z71.89');
        reordered = [...rest, ...z89];
        onReorder(reordered);
      }
    }
  };

  const handleTogglePrimary = (code) => {
    if (!setPrimaryIcd10Codes) return;
    if (primaryIcd10Codes.includes(code)) {
      setPrimaryIcd10Codes(prev => prev.filter(c => c !== code));
    } else {
      setPrimaryIcd10Codes(prev => [...prev, code]);
    }
  };

  const canDrag = !!onReorder;
  const [collapsed, setCollapsed] = useState(false);
  // Feb 27, 2026 — Top 3 + "Show all" expander. The ICD-10 panel was
  // growing taller than the clinical-note preview when the model returned
  // 6-8 codes; provider only acts on the top 3 (primary + maybe one
  // secondary) the vast majority of the time. Default: show first 3,
  // collapsible chevron expands the rest. Manual codes always shown
  // (the provider just added them, they need to see them confirmed).
  const [showAllCodes, setShowAllCodes] = useState(false);
  const TOP_N = 3;
  const visibleCodes = (() => {
    if (showAllCodes || sortedCodes.length <= TOP_N) return sortedCodes;
    const manuals = sortedCodes.filter(c => c.relevance === 'manual');
    const ai = sortedCodes.filter(c => c.relevance !== 'manual');
    return [...ai.slice(0, TOP_N), ...manuals];
  })();
  const hiddenCount = sortedCodes.length - visibleCodes.length;

  if (sortedCodes.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2 p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
      <div className="flex items-center justify-between">
        <button 
          className="text-xs font-bold text-emerald-400 uppercase tracking-wider flex items-center gap-2 hover:opacity-80 transition-opacity"
          onClick={() => setCollapsed(!collapsed)}
          data-testid="icd10-collapse-toggle"
        >
          <Hash className="w-3.5 h-3.5" />
          ICD-10 Codes ({sortedCodes.length})
          <ChevronDown className={`w-3 h-3 transition-transform ${collapsed ? '' : 'rotate-180'}`} />
        </button>
        <Button variant="ghost" size="sm" onClick={() => onCopy(getAllCodesText(), 'codes')}
          className={`h-8 px-3 text-muted-foreground hover:text-primary hover:bg-primary/10 ${copiedField === 'codes' ? 'copy-success' : ''}`}
          data-testid="copy-all-icd10-btn"
          title="Copy all ICD-10 codes">
          {copiedField === 'codes' ? <Check className="w-4 h-4 mr-1 text-green-600" /> : <Copy className="w-4 h-4 mr-1" />}
          {copiedField === 'codes' ? 'Copied!' : 'Copy All'}
        </Button>
      </div>

      {!collapsed && (canDrag ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={visibleCodes.map(c => c.code)} strategy={rectSortingStrategy}>
            <div className="flex flex-wrap gap-2" data-testid="icd10-output-sortable-list">
              {visibleCodes.map((code, index) => (
                <SortableCodePill
                  key={code.code}
                  code={code}
                  index={index}
                  isPrimary={primaryIcd10Codes.includes(code.code)}
                  isManual={code.relevance === 'manual'}
                  onDelete={onDelete}
                  onTogglePrimary={handleTogglePrimary}
                  resolvedDesc={resolvedDescs[code.code.toUpperCase()]}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="flex flex-wrap gap-2">
          {visibleCodes.map((code, index) => {
            const isPrimary = primaryIcd10Codes.includes(code.code);
            const isManual = code.relevance === 'manual';
            const displayDesc = (isManual && resolvedDescs[code.code.toUpperCase()]) ? resolvedDescs[code.code.toUpperCase()] : code.description;
            return (
              <div 
                key={index} 
                className={`flex items-center gap-1.5 px-2 py-1 border rounded transition-opacity ${
                  isManual ? 'bg-blue-500/10 border-blue-500/30' :
                  isPrimary ? 'bg-amber-500/10 border-amber-500/30' : 'bg-emerald-500/10 border-emerald-500/30'
                }`} 
                data-testid={`icd10-code-${index}`}
                title={displayDesc}
              >
                <button
                  className="flex items-center gap-1 cursor-pointer hover:opacity-80"
                  onClick={() => handleTogglePrimary(code.code)}
                >
                  {isPrimary && <span className="text-amber-400 font-bold text-xs">1°</span>}
                  <span className={`font-mono text-xs font-medium ${
                    isManual ? 'text-blue-400' : isPrimary ? 'text-amber-400' : 'text-emerald-400'
                  }`}>
                    {code.code}
                  </span>
                  {isManual && resolvedDescs[code.code.toUpperCase()] && (
                    <span className="text-[9px] text-blue-300/70 ml-0.5 max-w-[120px] truncate">{resolvedDescs[code.code.toUpperCase()]}</span>
                  )}
                </button>
                {onDelete && (
                  <button
                    className="shrink-0 cursor-pointer hover:text-red-400 text-muted-foreground transition-colors"
                    onClick={() => onDelete(code.code)}
                    title="Remove code"
                    data-testid={`icd10-delete-${code.code}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ))}
      
      {!collapsed && hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setShowAllCodes((v) => !v)}
          className="text-[10px] text-emerald-300 hover:text-emerald-200 font-semibold underline-offset-2 hover:underline"
          data-testid="icd10-show-all-toggle"
        >
          {showAllCodes ? `Show top ${TOP_N} only` : `+ Show all (${hiddenCount} more)`}
        </button>
      )}

      {!collapsed && canDrag && sortedCodes.length > 1 && (
        <p className="text-[10px] text-muted-foreground italic">Drag to reorder. Click code to mark as primary (1°). X to remove.</p>
      )}
    </div>
  );
};

export default ICD10CodesDisplay;
